// src/pages/health/HealthTrackingPage.tsx
// Main health tracking page with dashboard and navigation

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Activity,
  FileText,
  Clock,
  Download,
  Plus,
  TrendingUp,
  Calendar,
  Pill,
  ChevronRight,
} from 'lucide-react';
import { HealthDashboard } from '@/features/health/components/dashboard/HealthDashboard';
import { useChildren } from '@/features/children/hooks/useChildren';

interface QuickAction {
  id: string;
  label: string;
  description: string;
  icon: React.ElementType;
  href: string;
  color: string;
}

const quickActions: QuickAction[] = [
  {
    id: 'log',
    label: 'Log Entry',
    description: 'Record a new health entry',
    icon: Plus,
    href: 'log/new',
    color: 'sage',
  },
  {
    id: 'timeline',
    label: 'Timeline',
    description: 'View health history',
    icon: Clock,
    href: 'timeline',
    color: 'navy',
  },
  {
    id: 'reports',
    label: 'Reports',
    description: 'Generate health reports',
    icon: FileText,
    href: 'reports',
    color: 'terracotta',
  },
  {
    id: 'export',
    label: 'Export',
    description: 'Export health data',
    icon: Download,
    href: 'export',
    color: 'warm-gray',
  },
];

export const HealthTrackingPage: React.FC = () => {
  const { familyId, childId } = useParams<{ familyId: string; childId: string }>();
  const navigate = useNavigate();
  const { children, isLoading: childrenLoading } = useChildren(familyId || '');
  
  const [selectedChildId, setSelectedChildId] = useState<string>(childId || '');
  
  const selectedChild = children?.find((c) => c.id === selectedChildId);

  const handleChildChange = (newChildId: string) => {
    setSelectedChildId(newChildId);
    navigate(`/family/${familyId}/children/${newChildId}/health`);
  };

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; text: string; hover: string }> = {
      sage: {
        bg: 'bg-sage/10',
        text: 'text-sage-dark',
        hover: 'hover:bg-sage/20',
      },
      navy: {
        bg: 'bg-navy/10',
        text: 'text-navy',
        hover: 'hover:bg-navy/20',
      },
      terracotta: {
        bg: 'bg-terracotta/10',
        text: 'text-terracotta',
        hover: 'hover:bg-terracotta/20',
      },
      'warm-gray': {
        bg: 'bg-warm-gray/10',
        text: 'text-warm-gray',
        hover: 'hover:bg-warm-gray/20',
      },
    };
    return colors[color] || colors.sage;
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="bg-white border-b border-warm-gray/10 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-sage/10 rounded-xl">
                <Activity className="w-6 h-6 text-sage" />
              </div>
              <div>
                <h1 className="text-xl font-display font-semibold text-navy">
                  Health Tracking
                </h1>
                {selectedChild && (
                  <p className="text-sm text-warm-gray">
                    Tracking for {selectedChild.firstName}
                  </p>
                )}
              </div>
            </div>

            {/* Child Selector */}
            {children && children.length > 1 && (
              <select
                value={selectedChildId}
                onChange={(e) => handleChildChange(e.target.value)}
                className="px-4 py-2 bg-cream border border-warm-gray/20 rounded-xl
                         text-navy focus:outline-none focus:ring-2 focus:ring-sage/30"
              >
                {children.map((child) => (
                  <option key={child.id} value={child.id}>
                    {child.firstName} {child.lastName}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Actions */}
        <section className="mb-8">
          <h2 className="text-lg font-display font-semibold text-navy mb-4">
            Quick Actions
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {quickActions.map((action, index) => {
              const colors = getColorClasses(action.color);
              const Icon = action.icon;
              
              return (
                <motion.div
                  key={action.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={action.href}
                    className={`block p-4 bg-white rounded-2xl border border-warm-gray/10
                             ${colors.hover} transition-all duration-200 group`}
                  >
                    <div className={`p-3 ${colors.bg} rounded-xl w-fit mb-3`}>
                      <Icon className={`w-5 h-5 ${colors.text}`} />
                    </div>
                    <h3 className="font-medium text-navy group-hover:text-sage-dark transition-colors">
                      {action.label}
                    </h3>
                    <p className="text-sm text-warm-gray mt-1">
                      {action.description}
                    </p>
                    <ChevronRight className="w-4 h-4 text-warm-gray/50 mt-2 
                                           group-hover:translate-x-1 transition-transform" />
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Health Dashboard */}
        {selectedChildId && (
          <HealthDashboard childId={selectedChildId} />
        )}

        {/* Additional Navigation */}
        <section className="mt-8 grid md:grid-cols-3 gap-6">
          <Link
            to="medications"
            className="p-6 bg-white rounded-2xl border border-warm-gray/10
                     hover:shadow-soft transition-shadow group"
          >
            <div className="flex items-start justify-between">
              <div className="p-3 bg-terracotta/10 rounded-xl">
                <Pill className="w-6 h-6 text-terracotta" />
              </div>
              <ChevronRight className="w-5 h-5 text-warm-gray/50 
                                      group-hover:translate-x-1 transition-transform" />
            </div>
            <h3 className="font-display font-semibold text-navy mt-4">
              Medications
            </h3>
            <p className="text-sm text-warm-gray mt-1">
              Manage medications and track doses
            </p>
          </Link>

          <Link
            to="appointments"
            className="p-6 bg-white rounded-2xl border border-warm-gray/10
                     hover:shadow-soft transition-shadow group"
          >
            <div className="flex items-start justify-between">
              <div className="p-3 bg-navy/10 rounded-xl">
                <Calendar className="w-6 h-6 text-navy" />
              </div>
              <ChevronRight className="w-5 h-5 text-warm-gray/50 
                                      group-hover:translate-x-1 transition-transform" />
            </div>
            <h3 className="font-display font-semibold text-navy mt-4">
              Appointments
            </h3>
            <p className="text-sm text-warm-gray mt-1">
              View upcoming medical appointments
            </p>
          </Link>

          <Link
            to="trends"
            className="p-6 bg-white rounded-2xl border border-warm-gray/10
                     hover:shadow-soft transition-shadow group"
          >
            <div className="flex items-start justify-between">
              <div className="p-3 bg-sage/10 rounded-xl">
                <TrendingUp className="w-6 h-6 text-sage" />
              </div>
              <ChevronRight className="w-5 h-5 text-warm-gray/50 
                                      group-hover:translate-x-1 transition-transform" />
            </div>
            <h3 className="font-display font-semibold text-navy mt-4">
              Trends & Analytics
            </h3>
            <p className="text-sm text-warm-gray mt-1">
              View detailed health trends over time
            </p>
          </Link>
        </section>
      </main>
    </div>
  );
};

export default HealthTrackingPage;
